import { useEffect, useState, type ReactNode } from "react";
import { Eye, FileText, Flame, Globe, House, Images, Plus, SlidersHorizontal, User } from "lucide-react";
import { useCustom, useCustomMutation, usePermissions, useTranslate } from "@refinedev/core";
import { Button, LockIcon, Modal, TileBoard, UnlockIcon, type TileBoardItem } from "@fawkes/ui";

import {
  FacturenTile,
  FacturenTotalenDialog,
  MediaTile,
  PersoonsgegevensTile,
  RelatieVeldenDialog,
  VerblijvenTile,
  type RelatieDetail as RelatieDetailData,
} from "./relatieTiles";
import { CrematiesTile } from "./CrematiesTile";
import { WhatsAppLogo, WhatsAppTile } from "./WhatsAppTile";
import { KlantportaalTile } from "./KlantportaalTile";
import { useModuleAvailability } from "../api/modules";
import { useWerkmodus } from "../components/werkmodus";

interface TileLayout {
  order: string[];
  hidden: string[];
}

interface TilePreference {
  tileKey: string;
  visible: boolean;
  displayOrder: number;
}

// The Algemeen board (Persoonsgegevens/Facturen/Verblijven/Media) supports a lock/unlock layout-edit
// mode; the order + hidden set are a per-user preference persisted server-side (the Jmix-owned
// `user_relatie_tile_preference` table via GET/PUT /api/relaties/tile-preferences), so the layout
// follows the user across browsers — shared byte-for-byte with the legacy screen via the canonical
// tile-key vocabulary below. Nothing is pinned: Jmix's "Tegels tonen of verbergen" lets every tile be
// switched off, Relatiegegevens included.
const TILE_KEYS = ["persoonsgegevens", "facturen", "verblijven", "crematies", "media", "klantportaal",
  "whatsapp"] as const;

// React board key ↔ canonical Jmix tile key (the `user_relatie_tile_preference.tile_key` vocabulary).
const SERVER_KEY: Record<string, string> = {
  persoonsgegevens: "PERSOONSGEGEVENS",
  facturen: "FACTUREN",
  verblijven: "VERBLIJVEN",
  media: "MEDIA",
  klantportaal: "KLANTPORTAAL",
  whatsapp: "WHATSAPP",
};
const REACT_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(SERVER_KEY).map(([reactKey, serverKey]) => [serverKey, reactKey]),
);

const DEFAULT_LAYOUT: TileLayout = { order: [...TILE_KEYS], hidden: [] };

function reconcileOrder(order: string[]): string[] {
  const known = order.filter((k) => (TILE_KEYS as readonly string[]).includes(k));
  const missing = TILE_KEYS.filter((k) => !known.includes(k));
  return [...known, ...missing];
}

// Map the server's per-tile rows onto this board's order + hidden set.
function serverToLayout(prefs: TilePreference[]): TileLayout {
  const known = prefs
    .filter((p) => REACT_KEY[p.tileKey] !== undefined)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const order = reconcileOrder(known.map((p) => REACT_KEY[p.tileKey]));
  const hidden = known.filter((p) => !p.visible).map((p) => REACT_KEY[p.tileKey]);
  return { order, hidden };
}

/**
 * Zet de nieuwe volgorde van de getoonde tegels terug in de volledige volgorde. Tegels die dit scherm
 * weglaat houden hun plek, zodat het relatiescherm ze terugvindt waar ze stonden.
 */
function voegVolgordeSamen(volledig: string[], getoond: string[]): string[] {
  const rest = [...getoond];
  return volledig.map((k) => (getoond.includes(k) ? rest.shift()! : k));
}

// Full PUT payload: renumber displayOrder in steps of 10 (Jmix parity).
function layoutToPreferences(layout: TileLayout): TilePreference[] {
  const hiddenSet = new Set(layout.hidden);
  return layout.order.map((reactKey, i) => ({
    tileKey: SERVER_KEY[reactKey],
    visible: !hiddenSet.has(reactKey),
    displayOrder: (i + 1) * 10,
  }));
}

/**
 * Het tegelbord van een relatie — Relatiegegevens, Facturen, Verblijven, Crematies en Media — met de
 * knoppen om het in te richten en de vensters die erbij horen. Het volledige relatiescherm en het
 * tabblad Relatie van het pension-klantscherm tonen hetzelfde bord, met dezelfde bewaarde indeling.
 *
 * <p>Een hook in plaats van een component, omdat het relatiescherm de knoppen naast zijn tabbladen zet
 * en het bord eronder: de drie delen komen los terug, zodat elk scherm ze kan plaatsen waar het wil.
 */
export function useRelatieTegelbord({
  id,
  detail: d,
  onOpenFactuur,
  onNieuweFactuur,
  onOpenDier,
  onOpenUitvaart,
  onNieuweCrematie,
  zonder = [],
  titels = {},
}: {
  /**
   * Een eigen titel per tegel, al vertaald. Het pension-klantscherm noemt Verblijven "Asielverblijven",
   * omdat daar ook pensionverblijven staan.
   */
  titels?: Record<string, string>;
  id: string;
  /**
   * Tegels die dit scherm niet toont omdat het ze elders al heeft — het pension-klantscherm heeft een
   * eigen tabblad Facturen. De bewaarde indeling houdt ze gewoon, voor het relatiescherm.
   */
  zonder?: string[];
  /** Leeg zolang de relatie laadt; het bord blijft dan leeg. */
  detail: RelatieDetailData | undefined;
  onOpenFactuur?: (factuurId: string) => void;
  /** Een factuur beginnen voor deze klant -- een leeg scherm, nog geen factuur. */
  onNieuweFactuur?: (klantId: string) => void;
  onOpenDier?: (dierId: string) => void;
  /** Vanuit de tegel Crematies een uitvaartdossier openen. */
  onOpenUitvaart?: (dossierId: string) => void;
  /** Een uitvaart beginnen voor deze klant. */
  onNieuweCrematie?: () => void;
}): { knoppen: ReactNode; bord: ReactNode; vensters: ReactNode } {
  const t = useTranslate();
  const [editing, setEditing] = useState(false);
  // Jmix reveals a per-tile settings icon once the layout is unlocked; the relation card’s opens
  // "Velden tonen of verbergen".
  const [veldenOpen, setVeldenOpen] = useState(false);
  const [totalenOpen, setTotalenOpen] = useState(false);
  const [tegelsOpen, setTegelsOpen] = useState(false);
  const [layout, setLayout] = useState<TileLayout>(DEFAULT_LAYOUT);
  // Staged switches for "Tegels tonen of verbergen": seeded when the dialog opens, written on Opslaan.
  const [tegelConcept, setTegelConcept] = useState<Record<string, boolean>>({});

  // Per-user tile layout, persisted server-side (user-scoped; one layout across all relations).
  const { data: prefData, refetch: refetchPrefs } = useCustom({ url: "relaties/tile-preferences", method: "get" });
  const prefs = prefData?.data as TilePreference[] | undefined;
  const { mutate: savePrefs } = useCustomMutation();

  // "Nieuwe factuur" opent het factuurscherm voor deze klant; regels, notities en betalingen horen
  // daar thuis. Dezelfde bevoegdheid als crediteren en dupliceren: alle drie brengen een nieuwe
  // factuur tot stand.
  const { data: permissions } = usePermissions<string[]>();
  const magFactuurMaken = Array.isArray(permissions) && permissions.includes("ROLE_FACTUUR_LIFECYCLE");
  // "Nieuwe factuur" opent een leeg factuurscherm; de factuur zelf ontstaat pas als je daar op
  // Opslaan drukt. Hier meteen een concept aanmaken liet bij elke misklik een lege factuur
  // achter in de boekhouding (Maarten, 2026-10-24).
  const nieuweFactuur = () => onNieuweFactuur?.(id);

  useEffect(() => {
    if (prefs) setLayout(serverToLayout(prefs));
  }, [prefs]);

  // Re-seed each time the dialog opens so a cancelled edit never lingers.
  useEffect(() => {
    if (tegelsOpen) {
      setTegelConcept(Object.fromEntries(layout.order.map((k) => [k, !layout.hidden.includes(k)])));
    }
  }, [tegelsOpen, layout]);

  const persist = (next: TileLayout) => {
    setLayout(next);
    savePrefs(
      {
        url: "relaties/tile-preferences",
        method: "put",
        values: { preferences: layoutToPreferences(next) },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => refetchPrefs(), onError: () => refetchPrefs() },
    );
  };

  const { isModuleEnabled } = useModuleAvailability();
  const [werkmodus] = useWerkmodus();
  // In de crematoriummodus blijven de asieldelen weg; zie RelatieDetail.
  const asielDelen = werkmodus !== "crematorium";

  // Jmix titles this card "Relatiegegevens" and puts Persoons-/Bedrijfsgegevens inside it as the
  // first section — the card holds the address, contact details, roles and tags too, so naming the
  // whole thing after one of its sections read wrong.
  const persoonsgegevensTitle = t("relatieDetailView.tile.relatiegegevens");

  // Every Jmix tile leads with an icon and carries its own settings button in the header, revealed
  // once the layout is unlocked. Of the four, only Relatiegegevens and Facturen open anything: the
  // Verblijven and Media buttons have no click listener at all in Jmix, so they are rendered
  // disabled here rather than pretending to work.
  const tegelTitel = (
    Icon: typeof User,
    label: string,
    knop?: { label: string; onClick?: () => void },
    extra?: ReactNode,
  ) => (
    <span className="fawkes-relatie-tiletitel">
      <Icon size={18} aria-hidden="true" />
      {label}
      {extra}
      {editing && knop && (
        <button
          type="button"
          className="fawkes-relatie-tiletitel__knop"
          aria-label={knop.label}
          title={knop.label}
          disabled={!knop.onClick}
          onClick={knop.onClick}
        >
          <SlidersHorizontal size={16} aria-hidden="true" />
        </button>
      )}
    </span>
  );

  const tiles: TileBoardItem[] = d
    ? [
        {
          key: "persoonsgegevens",
          title: tegelTitel(User, persoonsgegevensTitle, {
            label: t("relatieDetailView.velden.knop"),
            onClick: () => setVeldenOpen(true),
          }),
          content: <PersoonsgegevensTile detail={d} />,
        },
        {
          key: "facturen",
          title: tegelTitel(
            FileText,
            t("relatieDetailView.tile.facturen"),
            { label: t("relatieDetailView.facturen.totalenKnop"), onClick: () => setTotalenOpen(true) },
            // Jmix zet "Nieuwe factuur" naast de tegeltitel maar deed er niets mee. Hier opent hij het
            // factuurscherm voor deze klant, zodat je er meteen regels op kunt zetten.
            <button
              type="button"
              className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
              disabled={!magFactuurMaken || !onNieuweFactuur}
              title={magFactuurMaken ? undefined : t("relatieDetailView.facturen.nieuweGeenRecht")}
              onClick={nieuweFactuur}
            >
              <Plus size={14} aria-hidden="true" />
              {t("relatieDetailView.facturen.nieuwe")}
            </button>,
          ),
          content: <FacturenTile relatieId={id} onOpenFactuur={onOpenFactuur} />,
        },
        // The Verblijven tile belongs to the asiel module (Jmix RelatieEntryView hides it when asiel is off).
        ...(asielDelen && isModuleEnabled("asiel")
          ? [{
              key: "verblijven",
              title: tegelTitel(House, titels.verblijven ?? t("relatieDetailView.tile.verblijven"), {
                label: t("relatieDetailView.verblijven.instellingenKnop"),
              }),
              content: <VerblijvenTile relatieId={id} onOpenDier={onOpenDier} />,
            }]
          : []),
        // Crematorium: de uitvaarten van deze klant. Alleen in beeld als de module aan staat — een
        // asiel zonder crematorium heeft er niets aan.
        ...(isModuleEnabled("crematorium")
          ? [{
              key: "crematies",
              title: tegelTitel(
                Flame,
                t("relatieDetailView.tile.crematies"),
                undefined,
                // Naast de titel, in dezelfde vorm als "Nieuwe factuur": een uitvaart beginnen is een
                // actie op de tegel, niet iets wat onder de lijst hoort weg te zakken.
                onNieuweCrematie ? (
                  <button
                    type="button"
                    className="fawkes-relatie-tiletitel__actie fawkes-relatie-tiletitel__actie--primair"
                    onClick={onNieuweCrematie}
                  >
                    <Plus size={14} aria-hidden="true" />
                    {t("crematiesTile.nieuw")}
                  </button>
                ) : undefined,
              ),
              content: <CrematiesTile relatieId={id} onOpenDossier={onOpenUitvaart} />,
            }]
          : []),
        // Klantportaal: wie van deze klant in het portaal kan, en uitnodigen. Alleen in pensionmodus; een asiel
        // heeft (nog) geen klantportaal.
        ...(werkmodus === "pension" && isModuleEnabled("pension")
          ? [{
              key: "klantportaal",
              title: tegelTitel(Globe, t("relatieDetailView.tile.klantportaal")),
              content: <KlantportaalTile relatieId={id} />,
            }]
          : []),
        // WhatsApp: appen met deze klant vanaf het nummer van de vestiging. Naast Klantportaal, want
        // het is dezelfde soort vraag -- hoe bereik je deze klant -- en je wilt het zien zonder te
        // zoeken. Of het nummer wél WhatsApp heeft weet niemand vooraf; de tegel probeert het en
        // zegt wat Meta terugmeldt.
        {
          key: "whatsapp",
          title: tegelTitel(WhatsAppLogo as unknown as typeof User, t("relatieDetailView.tile.whatsapp")),
          content: <WhatsAppTile relatieId={id} />,
        },
        {
          key: "media",
          title: tegelTitel(Images, t("relatieDetailView.tile.media"), {
            label: t("relatieDetailView.media.instellingenKnop"),
          }),
          content: <MediaTile relatieId={id} />,
        },
      ]
    : [];

  const getoond = (key: string) => !zonder.includes(key);
  const getoondeTegels = tiles.filter((tile) => getoond(tile.key));

  const layoutToggleTitle = editing ? t("animalDetailView.layout.done") : t("animalDetailView.layout.edit");

  const knoppen = (
    <span className="fawkes-relatie-detail__boardknoppen">
      <Button
        variant="secondary"
        className={`fawkes-relatie-detail__layout-toggle${editing ? " fawkes-relatie-detail__layout-toggle--active" : ""}`}
        aria-label={layoutToggleTitle}
        aria-pressed={editing}
        title={layoutToggleTitle}
        onClick={() => setEditing((e) => !e)}
      >
        {editing ? <UnlockIcon width={18} height={18} /> : <LockIcon width={18} height={18} />}
      </Button>
      {/* Jmix's second board button: the tiles themselves, switch per tile, in a dialog. */}
      <Button
        variant="secondary"
        className="fawkes-relatie-detail__layout-toggle"
        aria-label={t("relatieDetailView.tegels.knop")}
        title={t("relatieDetailView.tegels.knop")}
        onClick={() => setTegelsOpen(true)}
      >
        <Eye size={18} aria-hidden="true" />
      </Button>
    </span>
  );

  const bord = d ? (
    <TileBoard
      items={getoondeTegels}
      order={layout.order.filter(getoond)}
      hidden={layout.hidden.filter(getoond)}
      editing={editing}
      onReorder={(order) => persist({ ...layout, order: voegVolgordeSamen(layout.order, order) })}
      onToggleHidden={(key, hidden) =>
        persist({
          ...layout,
          hidden: hidden ? [...layout.hidden, key] : layout.hidden.filter((k) => k !== key),
        })
      }
      labels={{
        hide: t("animalDetailView.layout.hide"),
        drag: t("animalDetailView.layout.drag"),
        hiddenSection: t("animalDetailView.layout.hiddenSection"),
      }}
    />
  ) : null;

  const vensters = (
    <>
      <RelatieVeldenDialog open={veldenOpen} onClose={() => setVeldenOpen(false)} />
      <FacturenTotalenDialog open={totalenOpen} onClose={() => setTotalenOpen(false)} />

      {/* "Tegels tonen of verbergen" — the same per-user layout the board writes, edited as a list of
          switches and only saved on Opslaan, as in Jmix. Nothing is pinned there, so nothing is here. */}
      <Modal
        open={tegelsOpen}
        title={t("relatieDetailView.tegels.titel")}
        onClose={() => setTegelsOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTegelsOpen(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => {
                // Wat dit scherm niet toont, houdt de zichtbaarheid die het had.
                persist({
                  ...layout,
                  hidden: layout.order.filter((k) => (getoond(k) ? !tegelConcept[k] : layout.hidden.includes(k))),
                });
                setTegelsOpen(false);
              }}
            >
              {t("common.save")}
            </Button>
          </>
        }
      >
        <div className="fawkes-pleeg-cap__categorieen fawkes-relatie-velden">
          {layout.order.filter(getoond).map((key) => (
            <button
              key={key}
              type="button"
              role="switch"
              aria-checked={tegelConcept[key] ?? true}
              className={`fawkes-pleeg-cap__cat${(tegelConcept[key] ?? true) ? " is-on" : ""}`}
              onClick={() => setTegelConcept((c) => ({ ...c, [key]: !(c[key] ?? true) }))}
            >
              <span className="fawkes-pleeg-cap__knop" aria-hidden="true" />
              {titels[key] ?? (key === "persoonsgegevens" ? persoonsgegevensTitle : t(`relatieDetailView.tile.${key}`))}
            </button>
          ))}
        </div>
      </Modal>
    </>
  );

  return { knoppen, bord, vensters };
}
